import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Check, AlertCircle, KeyRound, Trash2, Loader2, ShieldCheck, X, Zap } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import { getAIConfig, patchAIConfig, testBYOMConfig } from '../../utils/accountApi';
import ConfirmModal from '../ConfirmModal';

// Map a thrown accountApi error to { code, status, message }. The backend error
// body is { error: { code, message } }; request() attaches it as err.data.
function readError(err) {
    return {
        status: err?.status,
        code: err?.data?.error?.code || '',
        message: err?.message || 'Something went wrong.',
        data: err?.data || null,
    };
}

// Friendly, action-appropriate text for the WP-13 error codes.
function messageForError(err) {
    const { code, status, message } = readError(err);
    switch (code) {
        case 'BYOM_MODEL_REQUIRED':
            return 'Enter the model name your endpoint expects.';
        case 'BYOM_NOT_ALLOWED':
            return 'Custom models are available on the Professional and Enterprise plans.';
        case 'BYOM_UNAVAILABLE':
            // 503: AI_CONFIG_ENC_KEY not provisioned. Not the user's key — say so.
            return 'Custom models aren’t available yet — the platform isn’t configured for them. Your key was not the problem.';
        case 'BYOM_KEY_INVALID':
            return 'That key could not be saved. Check it and try again.';
        case 'ENGINE_NOT_ALLOWED':
            return 'That engine isn’t available on your current plan.';
        case 'TIER_LOOKUP_FAILED':
            return 'We couldn’t verify your plan just now. Your current engine is unchanged — please try again.';
        case 'BYOM_ENDPOINT_REQUIRED':
            return 'Enter the endpoint URL your custom model is served from.';
        case 'BYOM_ACTIVE':
            return 'Remove your custom model before switching to a listed engine.';
        case 'BYOM_NOT_CONFIGURED':
            return 'Enter a model name, endpoint and key first.';
        case 'BYOM_TEST_RATE_LIMITED':
            return 'That’s a lot of tests in a short time. Wait a moment and try again.';
        default:
            return status ? `${message}` : message;
    }
}

// The endpoint policy and connection test return their own coded messages, and
// those messages are already written for a customer to read. Only the codes that
// need extra guidance are rewritten here.
function messageForTestCode(code, fallback) {
    switch (code) {
        case 'BYOM_ENDPOINT_SCHEME':
            return 'The endpoint URL must start with https:// — plain http isn’t accepted.';
        case 'BYOM_ENDPOINT_PRIVATE':
            return 'That endpoint points at a private or internal address, which our servers will not call.';
        case 'BYOM_ENDPOINT_DNS':
            return 'We couldn’t look up that host name. Check the endpoint URL for a typo.';
        case 'BYOM_ENDPOINT_REDIRECT':
            return 'The endpoint redirected us somewhere else. Use the final URL directly.';
        case 'BYOM_AUTH_FAILED':
            return 'Your endpoint rejected the API key. Check the key and try again.';
        case 'BYOM_NOT_FOUND':
            return 'The endpoint returned “not found”. The URL usually ends in /v1.';
        case 'BYOM_CONTRACT_UNMET':
            return 'Your model answered, but not in the structured format surveys need. It may be too small to follow instructions reliably.';
        default:
            return fallback || 'The test did not succeed.';
    }
}



export function AccountAISection() {
    const { orgId } = useOrg();

    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [busyEngineId, setBusyEngineId] = useState(''); // catalog card mid-save
    const [actionError, setActionError] = useState('');   // inline error from a mutation
    const [notice, setNotice] = useState('');             // transient success note

    // BYOM form state
    const [byomModel, setByomModel] = useState('');
    const [byomKey, setByomKey] = useState('');
    const [byomEndpoint, setByomEndpoint] = useState('');
    const [byomFieldError, setByomFieldError] = useState('');       // model-required, inline
    const [byomKeyError, setByomKeyError] = useState('');           // key-required, inline
    const [byomEndpointError, setByomEndpointError] = useState(''); // endpoint-required, inline
    const [savingByom, setSavingByom] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // last connection-test outcome

    const load = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        setLoadError('');
        try {
            const data = await getAIConfig(orgId);
            setConfig(data);
        } catch (err) {
            setLoadError(messageForError(err));
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    // Select a catalog engine. Do NOT optimistically change local state — on error
    // (e.g. TIER_LOOKUP_FAILED) the current selection must stay put.
    const selectEngine = async (engineId) => {
        // Blocked while a BYOM key is stored (pending or approved): a catalog switch
        // would half-wipe the BYOM config server-side. Remove the custom model first.
        if (!config || engineId === config.model || busyEngineId || config.byom_configured) return;
        setBusyEngineId(engineId);
        setActionError('');
        setNotice('');
        try {
            await patchAIConfig(orgId, { model: engineId });
            await load();
            setNotice('Engine updated.');
        } catch (err) {
            setActionError(messageForError(err));
        } finally {
            setBusyEngineId('');
        }
    };

    // Test without saving. The server calls the endpoint and reports three
    // things: can it be reached, does it speak the OpenAI chat format, and can
    // the model produce the structured turn a survey needs. The third is the one
    // that catches a model which answers happily and then breaks every turn.
    const runTest = async () => {
        setActionError('');
        setNotice('');
        setTestResult(null);
        setTesting(true);
        try {
            const res = await testBYOMConfig(orgId, {
                model: byomModel.trim(),
                custom_endpoint_url: byomEndpoint.trim(),
                custom_api_key: byomKey,
            });
            setTestResult(res);
        } catch (err) {
            setActionError(messageForError(err));
        } finally {
            setTesting(false);
        }
    };

    const saveByom = async () => {
        setByomFieldError('');
        setByomKeyError('');
        setByomEndpointError('');
        setActionError('');
        setNotice('');
        setTestResult(null);
        if (!byomModel.trim()) {
            // Client-side required field; the 422 backstop is the server's job.
            setByomFieldError('Enter the model name your endpoint expects.');
            return;
        }
        if (!byomEndpoint.trim()) {
            // Required: the resolver builds the BYOM engine's base URL only from this.
            // Without it, an approved key breaks every turn with "missing baseURL".
            setByomEndpointError('Enter the endpoint URL your custom model is served from.');
            return;
        }
        if (!byomKey) {
            // Guard: an empty key would be sent as custom_api_key:"" — the server's
            // off-ramp (clear). Saving must never clear; the Remove button does that.
            setByomKeyError('Enter your API key. To remove a saved model, use Remove.');
            return;
        }
        setSavingByom(true);
        try {
            await patchAIConfig(orgId, {
                model: byomModel.trim(),
                custom_api_key: byomKey,
                custom_endpoint_url: byomEndpoint.trim(),
            });
            setByomKey('');
            await load();
            setNotice('Custom model saved. Your surveys are using it now.');
        } catch (err) {
            const { code, data } = readError(err);
            // The server tests the endpoint before it stores anything, so a save
            // can fail for a connection reason. Show that as a test result — it
            // is the same information the Test button would have given.
            if (data?.test) setTestResult(data.test);
            if (code === 'BYOM_MODEL_REQUIRED') setByomFieldError(messageForError(err));
            else if (code === 'BYOM_ENDPOINT_REQUIRED') setByomEndpointError(messageForError(err));
            else if (data?.test) setActionError('');
            else setActionError(messageForError(err));
        } finally {
            setSavingByom(false);
        }
    };

    const clearByom = async () => {
        setConfirmClear(false);
        setClearing(true);
        setActionError('');
        setNotice('');
        setTestResult(null);
        try {
            await patchAIConfig(orgId, { custom_api_key: '' }); // off-ramp
            await load();
            setNotice('Custom model removed. Surveys use your plan engine again.');
        } catch (err) {
            setActionError(messageForError(err));
        } finally {
            setClearing(false);
        }
    };

    if (loading) {
        return (
            <div>
                <SectionHeader />
                <div style={styles.centered}>
                    <Loader2 size={22} style={{ ...styles.accent, animation: 'spin 0.8s linear infinite' }} />
                    <span style={styles.mutedText}>Loading engine settings…</span>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div>
                <SectionHeader />
                <div style={styles.loadErrorBox}>
                    <AlertCircle size={18} color="#FCA5A5" />
                    <span style={{ flex: 1 }}>{loadError}</span>
                    <button style={styles.retryBtn} onClick={load}>Retry</button>
                </div>
            </div>
        );
    }

    if (!config) return <div><SectionHeader /></div>;

    const allowed = config.allowed_engines || [];
    const byomSupported = !!config.byom_supported;
    const byomConfigured = !!config.byom_configured;
    // A saved custom model is either working or it is not. Fall back to
    // byom_configured so this still renders against a server deployed before
    // byom_working existed.
    const byomActive = config.byom_working ?? byomConfigured;

    return (
        <div>
            <SectionHeader />

            {/* Current status */}
            <div style={styles.statusRow}>
                <span style={styles.tierChip}>{titleCase(config.tier)} plan</span>
                {byomConfigured && byomActive ? (
                    <span style={styles.statusText}>
                        Serving from your custom model <code style={styles.code}>{config.model}</code>
                    </span>
                ) : byomConfigured ? (
                    <span style={styles.statusText}>
                        Your custom model isn’t working. Surveys are using your plan engine.
                    </span>
                ) : (
                    <span style={styles.statusText}>
                        Active engine: <code style={styles.code}>{config.model}</code>
                    </span>
                )}
            </div>

            {actionError && (
                <div style={styles.inlineError}>
                    <AlertCircle size={16} color="#FCA5A5" />
                    <span>{actionError}</span>
                </div>
            )}
            {notice && <div style={styles.notice}><Check size={16} color="#34D399" /><span>{notice}</span></div>}

            {/* Engine selector cards */}
            <div style={styles.cardGrid}>
                {allowed.map((eng) => {
                    const isCurrent = eng.engine_id === config.model && !byomConfigured;
                    const isBusy = busyEngineId === eng.engine_id;
                    return (
                        <button
                            key={eng.engine_id}
                            onClick={() => selectEngine(eng.engine_id)}
                            disabled={!!busyEngineId || byomConfigured}
                            style={{
                                ...styles.card,
                                ...(isCurrent ? styles.cardActive : {}),
                                ...(byomConfigured ? styles.cardDimmed : {}),
                            }}
                        >
                            <div style={styles.cardTop}>
                                <div style={styles.cardIcon}><Bot size={20} color="#A5B4FC" /></div>
                                {isCurrent && <span style={styles.activeBadge}>● Active</span>}
                                {isBusy && <Loader2 size={16} style={{ ...styles.accent, marginLeft: 'auto', animation: 'spin 0.8s linear infinite' }} />}
                            </div>
                            <div style={styles.cardName}>{eng.display_name}</div>
                            <div style={styles.cardModel}>{eng.model_id}</div>

                        </button>
                    );
                })}
            </div>
            {byomConfigured && (
                <div style={styles.switchHint}>Remove your custom model to switch engines.</div>
            )}

            {/* BYOM */}
            {byomSupported && (
                <div style={styles.byomPanel}>
                    <div style={styles.byomHeader}>
                        <KeyRound size={16} color="#818CF8" />
                        <span style={styles.byomTitle}>Bring your own model</span>
                        {byomConfigured && (byomActive
                            ? <span style={styles.badgeApproved}><ShieldCheck size={13} /> In use</span>
                            : <span style={styles.badgePending}><AlertCircle size={13} /> Not working</span>
                        )}
                    </div>
                    <p style={styles.byomHelp}>
                        Use your own OpenAI-compatible endpoint and key. Keys are stored encrypted.
                        We check your endpoint when you save, and your surveys start using it right away.
                        Your model has no fallback, if your LLM fails then your surveys fail.
                    </p>

                    <div style={styles.field}>
                        <label style={styles.label}>Model name <span style={styles.req}>*</span></label>
                        <input
                            style={{ ...styles.input, ...(byomFieldError ? styles.inputError : {}) }}
                            value={byomModel}
                            onChange={(e) => setByomModel(e.target.value)}
                            placeholder="e.g. gpt-4o-mini"
                        />
                        {byomFieldError && <div style={styles.fieldError}>{byomFieldError}</div>}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>API key <span style={styles.req}>*</span></label>
                        <input
                            style={{ ...styles.input, ...(byomKeyError ? styles.inputError : {}) }}
                            type="password"
                            value={byomKey}
                            onChange={(e) => setByomKey(e.target.value)}
                            placeholder={byomConfigured ? '•••••••• (re-enter to replace)' : 'sk-…'}
                        />
                        {byomKeyError && <div style={styles.fieldError}>{byomKeyError}</div>}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Endpoint URL <span style={styles.req}>*</span></label>
                        <input
                            style={{ ...styles.input, ...(byomEndpointError ? styles.inputError : {}) }}
                            value={byomEndpoint}
                            onChange={(e) => setByomEndpoint(e.target.value)}
                            placeholder="https://api.your-host.com/v1"
                        />
                        {byomEndpointError && <div style={styles.fieldError}>{byomEndpointError}</div>}
                    </div>

                    <div style={styles.byomActions}>
                        <button style={styles.primaryBtn} onClick={saveByom} disabled={savingByom || testing}>
                            {savingByom ? 'Saving…' : byomConfigured ? 'Replace custom model' : 'Save custom model'}
                        </button>
                        <button style={styles.secondaryBtn} onClick={runTest} disabled={testing || savingByom}>
                            <Zap size={14} /> {testing ? 'Testing…' : 'Test connection'}
                        </button>
                        {byomConfigured && (
                            <button style={styles.dangerBtn} onClick={() => setConfirmClear(true)} disabled={clearing}>
                                <Trash2 size={14} /> {clearing ? 'Removing…' : 'Remove'}
                            </button>
                        )}
                    </div>

                    {testResult && <TestResultPanel result={testResult} onDismiss={() => setTestResult(null)} />}
                </div>
            )}

            <ConfirmModal
                open={confirmClear}
                title="Remove custom model?"
                message="This deletes your stored key and endpoint and reverts surveys to your plan's engine."
                confirmLabel="Remove"
                cancelLabel="Keep"
                danger
                onConfirm={clearByom}
                onCancel={() => setConfirmClear(false)}
            />
        </div>
    );
}

// Renders the outcome of a connection test as three checks plus the model's own
// words. The raw reply is rendered as plain text inside <pre>: it comes from a
// server we do not control, so it is never treated as markup. React escapes by
// default — this must not be changed to dangerouslySetInnerHTML.
function TestResultPanel({ result, onDismiss }) {
    const rows = [
        { ok: result.reachable, label: 'Connected and authenticated' },
        { ok: result.chat_shape, label: 'Returned a chat completion' },
        { ok: result.contract, label: 'Followed the format surveys need' },
    ];
    return (
        <div style={{ ...styles.testPanel, ...(result.ok ? styles.testPanelOk : styles.testPanelBad) }}>
            <div style={styles.testHeader}>
                <span style={{ fontWeight: 600, color: result.ok ? '#6EE7B7' : '#FCA5A5' }}>
                    {result.ok ? 'Your custom model is working' : 'The test did not pass'}
                </span>
                {typeof result.latency_ms === 'number' && result.latency_ms > 0 && (
                    <span style={styles.testLatency}>{result.latency_ms} ms</span>
                )}
                <button style={styles.dismissBtn} onClick={onDismiss} aria-label="Dismiss test result">
                    <X size={14} />
                </button>
            </div>

            <ul style={styles.testList}>
                {rows.map((r) => (
                    <li key={r.label} style={styles.testItem}>
                        {r.ok
                            ? <Check size={14} color="#34D399" style={{ flexShrink: 0 }} />
                            : <X size={14} color="#FCA5A5" style={{ flexShrink: 0 }} />}
                        <span style={{ color: r.ok ? '#CBD5E1' : '#94A3B8' }}>{r.label}</span>
                    </li>
                ))}
            </ul>

            {!result.ok && (
                <div style={styles.testMessage}>{messageForTestCode(result.code, result.message)}</div>
            )}
            {result.ok && result.extracted && (
                <div style={styles.testMessage}>
                    It read the test answer back as <code style={styles.code}>{result.extracted}</code>.
                </div>
            )}
            {result.sample_text && (
                <>
                    <div style={styles.testSampleLabel}>What your model replied</div>
                    <pre style={styles.testSample}>{result.sample_text}</pre>
                </>
            )}
        </div>
    );
}

function SectionHeader() {
    return (
        <>
            <h2 style={styles.title}>AI Engine</h2>
            <p style={styles.subtitle}>The AI engine powering your survey conversations and analysis.</p>
        </>
    );
}

function titleCase(s) {
    return String(s || '').replace(/\b\w/g, (c) => c.toUpperCase());
}

const styles = {
    title: { fontSize: 'var(--font-size-xl)', fontWeight: 600, color: '#E2E8F0', margin: '0 0 var(--spacing-xs)' },
    subtitle: { fontSize: 'var(--font-size-sm)', color: '#C3CDDC', margin: '0 0 var(--spacing-lg)' },

    centered: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', padding: 'var(--spacing-2xl) 0', justifyContent: 'center' },
    accent: { color: '#818CF8' },
    mutedText: { color: '#C3CDDC', fontSize: 'var(--font-size-sm)' },

    loadErrorBox: {
        display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)',
        padding: '14px 18px', borderRadius: 'var(--radius-lg)',
        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
        color: '#FCA5A5', fontSize: 'var(--font-size-sm)',
    },
    retryBtn: {
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
        color: '#E2E8F0', borderRadius: 'var(--radius-md)', padding: '4px 12px',
        cursor: 'pointer', fontSize: 'var(--font-size-xs)',
    },

    statusRow: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-base)', flexWrap: 'wrap' },
    tierChip: {
        fontSize: 'var(--font-size-xs)', fontWeight: 600, color: '#A5B4FC',
        background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 'var(--radius-full)', padding: '3px 12px',
    },
    statusText: { fontSize: 'var(--font-size-sm)', color: '#C3CDDC' },
    code: { fontFamily: 'var(--font-family-mono)', color: '#CBD5E1', background: 'rgba(148,163,184,0.1)', padding: '1px 6px', borderRadius: 'var(--radius-sm)' },

    inlineError: {
        display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)',
        padding: '10px 14px', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--spacing-md)',
        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
        color: '#FCA5A5', fontSize: 'var(--font-size-sm)',
    },
    notice: {
        display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)',
        padding: '10px 14px', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--spacing-md)',
        background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
        color: '#6EE7B7', fontSize: 'var(--font-size-sm)',
    },

    cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' },
    card: {
        textAlign: 'left', cursor: 'pointer',
        background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--spacing-base)', color: '#E2E8F0',
        transition: 'border-color 0.15s ease, background 0.15s ease',
    },
    cardActive: { background: 'rgba(99,102,241,0.14)', borderColor: 'rgba(129,140,248,0.7)' },
    cardDimmed: { opacity: 0.5, cursor: 'not-allowed' },
    cardTop: { display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-md)' },
    cardIcon: {
        width: 40, height: 40, borderRadius: 'var(--radius-lg)', background: 'rgba(99,102,241,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    activeBadge: {
        marginLeft: 'auto', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: '#34D399',
        background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
        borderRadius: 'var(--radius-full)', padding: '3px 10px',
    },
    cardName: { fontSize: 'var(--font-size-md)', fontWeight: 600, color: '#E2E8F0' },
    cardModel: { fontSize: 'var(--font-size-sm)', color: '#A3B1C6', fontFamily: 'var(--font-family-mono)', marginTop: 2, wordBreak: 'break-all' },

    switchHint: { fontSize: 'var(--font-size-sm)', lineHeight: 1.5, color: '#B4C0D3', margin: '0 0 var(--spacing-lg)' },

    byomPanel: {
        background: 'rgba(148,163,184,0.04)', border: '1px solid rgba(148,163,184,0.1)',
        borderRadius: 'var(--radius-xl)', padding: 'var(--spacing-lg)',
    },
    byomHeader: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' },
    byomTitle: { fontSize: 'var(--font-size-base)', fontWeight: 600, color: '#CBD5E1' },
    badgeApproved: {
        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 'var(--font-size-xs)', fontWeight: 600, color: '#34D399',
        background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
        borderRadius: 'var(--radius-full)', padding: '3px 10px',
    },
    badgePending: {
        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 'var(--font-size-xs)', fontWeight: 600, color: '#FBBF24',
        background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
        borderRadius: 'var(--radius-full)', padding: '3px 10px',
    },

    testPanel: {
        marginTop: 'var(--spacing-base)', padding: 'var(--spacing-base)',
        borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-sm)',
    },
    testPanelOk: { background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.22)' },
    testPanelBad: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.22)' },
    testHeader: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' },
    testLatency: { fontSize: 'var(--font-size-xs)', color: '#B4C0D3' },
    dismissBtn: {
        marginLeft: 'auto', background: 'transparent', border: 'none', color: '#94A3B8',
        cursor: 'pointer', display: 'inline-flex', padding: 2,
    },
    testList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 },
    testItem: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' },
    testMessage: { marginTop: 'var(--spacing-sm)', color: '#CBD5E1', lineHeight: 1.6 },
    testSampleLabel: { marginTop: 'var(--spacing-md)', fontSize: 'var(--font-size-xs)', color: '#B4C0D3', marginBottom: 4 },
    testSample: {
        margin: 0, padding: '10px 12px', maxHeight: 200, overflow: 'auto',
        background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.15)',
        borderRadius: 'var(--radius-md)', color: '#CBD5E1',
        fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    },
    byomHelp: { fontSize: 'var(--font-size-sm)', color: '#B4C0D3', lineHeight: 1.6, margin: '0 0 var(--spacing-base)' },
    field: { marginBottom: 'var(--spacing-md)' },
    label: { display: 'block', fontSize: 'var(--font-size-base)', fontWeight: 600, color: '#E2E8F0', marginBottom: 'var(--spacing-xs)' },
    req: { color: '#FBBF24' },
    optional: { color: '#A3B1C6' },
    input: {
        width: '100%', boxSizing: 'border-box', padding: '9px 12px',
        background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
        borderRadius: 'var(--radius-md)', color: '#E2E8F0', fontSize: 'var(--font-size-sm)',
        fontFamily: 'var(--font-family-primary)',
    },
    inputError: { borderColor: 'rgba(239,68,68,0.6)' },
    fieldError: { fontSize: 'var(--font-size-xs)', color: '#FCA5A5', marginTop: 'var(--spacing-xs)' },
    byomActions: { display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-base)' },
    primaryBtn: {
        background: '#4F46E5', border: 'none', color: '#fff', fontWeight: 600,
        borderRadius: 'var(--radius-md)', padding: '9px 18px', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
    },
    secondaryBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(148,163,184,0.25)',
        color: '#E2E8F0', fontWeight: 600,
        borderRadius: 'var(--radius-md)', padding: '9px 16px', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
    },
    dangerBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'transparent', border: '1px solid rgba(239,68,68,0.4)', color: '#FCA5A5',
        borderRadius: 'var(--radius-md)', padding: '9px 14px', cursor: 'pointer', fontSize: 'var(--font-size-sm)',
    },
};
